"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FaExpand, FaCompress } from "react-icons/fa";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

interface CarImageCarouselProps {
  images: string[];
}

const CarImageCarousel: React.FC<CarImageCarouselProps> = ({ images }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [zoomed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null); //  Conteneur des vignettes

  // Détection de la taille d'écran
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Empêcher le scroll en fullscreen
  useEffect(() => {
    document.body.style.overflow = fullscreen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [fullscreen]);

  const handleNextImage = () =>
    setCurrentImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const handlePrevImage = () =>
    setCurrentImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));

  const toggleFullscreen = () => setFullscreen((prev) => !prev);

  //  Scroll automatique de la vignette sélectionnée
  useEffect(() => {
    if (thumbnailContainerRef.current) {
      const selectedThumbnail = thumbnailContainerRef.current.children[
        currentImage
      ] as HTMLElement;
      if (selectedThumbnail) {
        selectedThumbnail.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }
    }
  }, [currentImage]);

  return (
    <div
      className={`car-carousel ${fullscreen ? "fullscreen" : ""}`}
      style={{
        position: fullscreen ? "fixed" : "relative",
        top: fullscreen ? 0 : "auto",
        left: fullscreen ? 0 : "auto",
        width: fullscreen ? "100vw" : "auto",
        height: fullscreen ? "100vh" : "auto",
        zIndex: fullscreen ? 9999 : "auto",
        display: "flex",
        flexDirection: fullscreen ? "column" : "row",
        justifyContent: "center",
        alignItems: "center",
        background: fullscreen ? "black" : "transparent",
      }}
    >
      {/* Image principale avec lazy loading et gestion hauteur/largeur */}
      <motion.div
        className={`carousel-image-container ${zoomed ? "zoomed" : ""}`}
        initial={{ scale: 1 }}
        animate={{ scale: zoomed ? 2 : 1 }}
        transition={{ duration: 0.5 }}
        style={{
          flexGrow: 1,
          position: "relative",
          cursor: zoomed ? "grab" : "pointer",
          overflow: zoomed ? "hidden" : "visible",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LazyLoadImage
          src={images[currentImage]}
          alt={`Car image ${currentImage}`}
          effect="blur"
          width={isSmallScreen ? "auto" : "100%"}
          height={isSmallScreen ? "100%" : "auto"}
          style={{
            objectFit: "cover",
            maxHeight: fullscreen ? "100vh" : "auto",
            cursor: "pointer",
          }}
        />
      </motion.div>

      {/*  Navigation */}
      <div className="carousel-controls">
        <motion.button
          className="prev-button"
          onClick={handlePrevImage}
          whileHover={{ scale: 1.1 }}
        >
          &#10094;
        </motion.button>
        <motion.button
          className="next-button"
          onClick={handleNextImage}
          whileHover={{ scale: 1.1 }}
        >
          &#10095;
        </motion.button>
      </div>

      {/* Thumbnails en mode fullscreen */}
      {fullscreen && (
        <div
          ref={thumbnailContainerRef}
          className="thumbnail-bar"
          style={{
            display: "flex",
            overflowX: "auto", //  Permet de scroller automatiquement
            width: "100%",
            justifyContent: "flex-start", //  Positionner à gauche
            padding: "10px",
            scrollbarWidth: "none", // Masquer la barre de scroll
          }}
        >
          {images.map((image, index) => (
            <LazyLoadImage
              key={index}
              src={image}
              alt={`Thumbnail ${index}`}
              effect="blur"
              width={80}
              height={60}
              onClick={() => setCurrentImage(index)}
              style={{
                objectFit: "cover",
                cursor: "pointer",
                border: currentImage === index ? "2px solid white" : "none",
                borderRadius: "5px",
                marginRight: "5px",
              }}
            />
          ))}
        </div>
      )}

      {/* Bouton Fullscreen */}
      <button className="fullscreen-button" onClick={toggleFullscreen}>
        {fullscreen ? <FaCompress size={20} /> : <FaExpand size={20} />}
      </button>
    </div>
  );
};

export default CarImageCarousel;
